import React, { useState } from 'react';
import { Box, Typography, Paper, Chip, Avatar, Table, TableBody, TableCell, TableHead, TableRow, TablePagination, useMediaQuery } from '@mui/material';
import type { ChipColor, ProgressColor } from '@/types/chip';
import { useNavigate } from 'react-router-dom';
import { Schedule, CheckCircle } from '@mui/icons-material';
import { ListPageHeader } from '@/components/ListPageHeader';
import { useEmployeeMap, getEmployeeName } from '@/hooks/useEmployeeMap';
import { useGetList } from 'react-admin';
import type { AlertRecord } from './constants';
import { STATUS_COLORS, ALERT_TYPE_COLORS } from './constants';

const ContractAlertSparkline = ({ triggerDate, status }: { triggerDate: string; status: string }) => {
  const [now] = useState(() => Date.now());
  if (status === 'Actioned' || status === 'Dismissed') {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CheckCircle sx={{ fontSize: 14, color: 'success.main' }} />
        <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 600 }}>Actioned</Typography>
      </Box>
    );
  }

  const due = new Date(triggerDate).getTime();
  const daysRemaining = Math.ceil((due - now) / (1000 * 60 * 60 * 24));

  const getColor = () => {
    if (daysRemaining <= 0) return 'error';
    if (daysRemaining <= 7) return 'error';
    if (daysRemaining <= 30) return 'warning';
    return 'success';
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 100 }}>
      <Typography variant="caption" sx={{ color: `${getColor()}.main`, fontWeight: 600 }}>
        {daysRemaining <= 0 ? 'Overdue' : `${daysRemaining}d`}
      </Typography>
    </Box>
  );
};

export const ContractAlertList = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const { data: alertItems, isLoading } = useGetList('contract-alerts', { pagination: { page: page + 1, perPage: rowsPerPage } });
  const isMobile = useMediaQuery((theme: { breakpoints: { down: (key: string) => string } }) => theme.breakpoints.down('md'));
  const employeeMap = useEmployeeMap();

  const [now] = useState(() => Date.now());
  const stats = {
    total: alertItems?.length ?? 0,
    pending: alertItems?.filter((a: AlertRecord) => a.status === 'Pending' || a.status === 'Triggered').length ?? 0,
    actioned: alertItems?.filter((a: AlertRecord) => a.status === 'Actioned').length ?? 0,
    dismissed: alertItems?.filter((a: AlertRecord) => a.status === 'Dismissed').length ?? 0,
  };

  const typeCounts: Record<string, number> = alertItems?.reduce((acc: Record<string, number>, a: AlertRecord) => {
    acc[a.alertType ?? ''] = (acc[a.alertType ?? ''] || 0) + 1;
    return acc;
  }, {}) ?? {};

  const pieData = Object.entries(typeCounts).map(([label, value]) => ({ label, value }));

  if (isMobile) {
    return (
      <Box sx={{ p: 2 }}>
        <ListPageHeader
          title="Contract Alerts"
          metrics={[
            { label: 'Pending', value: String(stats.pending), color: 'warning.main' },
            { label: 'Actioned', value: String(stats.actioned), color: 'success.main' },
            { label: 'Dismissed', value: String(stats.dismissed), color: 'default' },
          ]}
        />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 2 }}>
          {alertItems?.map((item: AlertRecord) => {
            const isPastDue = new Date(item.triggerDate ?? '') < new Date() && item.status !== 'Actioned';
            return (
              <Paper key={item.id} sx={{ p: 2 }} onClick={() => navigate(`/contract-alerts/${item.id}/show`)}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={600}>{getEmployeeName(employeeMap, item.employee?.id)}</Typography>
                  </Box>
                  <Chip label={item.status} size="small" color={STATUS_COLORS[item.status ?? ''] as ChipColor} />
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Chip label={item.alertType} size="small" color={ALERT_TYPE_COLORS[item.alertType ?? ''] as ChipColor} />
                  <Typography variant="caption" color={isPastDue ? 'error.main' : 'text.secondary'}>
                    Trigger: {item.triggerDate}
                  </Typography>
                </Box>
              </Paper>
            );
          })}
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <ListPageHeader
        title="Contract Alerts"
        metrics={[
          { label: 'Pending', value: String(stats.pending), color: 'warning.main' },
          { label: 'Actioned', value: String(stats.actioned), color: 'success.main' },
          { label: 'Dismissed', value: String(stats.dismissed), color: 'default' },
          { label: 'Total', value: String(stats.total), color: 'primary.main' },
        ]}
        pieChartData={pieData}
        pieChartTitle="By Alert Type"
      />
      <Paper sx={{ mt: 2, overflow: 'hidden' }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: 'action.hover' }}>
              <TableCell sx={{ fontWeight: 600 }}>Employee</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Trigger Date</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Action Required</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Time</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {alertItems?.map((item: AlertRecord) => {
              const isPastDue = new Date(item.triggerDate ?? '') < new Date() && item.status !== 'Actioned';
              return (
                <TableRow
                  key={item.id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/contract-alerts/${item.id}/show`)}
                >
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar sx={{ width: 28, height: 28, fontSize: 12, bgcolor: 'primary.main' }}>
                        {getEmployeeName(employeeMap, item.employee?.id).charAt(0)}
                      </Avatar>
                      <Typography variant="body2">{getEmployeeName(employeeMap, item.employee?.id)}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                  <Chip label={item.alertType} size="small" color={ALERT_TYPE_COLORS[item.alertType ?? '']} />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color={isPastDue ? 'error.main' : 'text.primary'}>
                      {item.triggerDate}
                    </Typography>
                  </TableCell>
                  <TableCell>
                  <Chip label={item.status} size="small" color={STATUS_COLORS[item.status ?? '']} />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {item.actionRequired}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <ContractAlertSparkline triggerDate={item.triggerDate ?? ''} status={item.status ?? ''} />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={alertItems?.length ?? 0}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          sx={{ borderTop: '1px solid', borderColor: 'divider' }}
        />
        {(!alertItems || alertItems.length === 0) && (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Schedule sx={{ fontSize: 48, color: 'grey.300', mb: 1 }} />
            <Typography color="text.secondary">No contract alerts found</Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
};
